/**
 * UnifiedEventTable
 *
 * Enhanced event table showing merged Temporal + custom telemetry events.
 * Features: source badges, collapsible activity groups, expandable JSON payloads,
 * filter bar, and virtual windowing for large event sets.
 */

import { useState, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronRight, Filter, Search, X } from 'lucide-react';
import type { UnifiedTimelineEvent, UnifiedEventSource, UnifiedCategory, UnifiedSeverity } from '../../api/telemetry';

interface UnifiedEventTableProps {
  events: UnifiedTimelineEvent[];
  className?: string;
  activeFilterLabel?: string;
  onClearExternalFilter?: () => void;
}

/* ── Constants ────────────────────────────────────────────────────── */

const PAGE_SIZE = 100;

const SOURCE_BADGE: Record<string, { label: string; bg: string; text: string }> = {
  'temporal-history': { label: 'Temporal', bg: 'bg-blue-50', text: 'text-blue-600' },
  'custom-telemetry': { label: 'Custom', bg: 'bg-neutral-50', text: 'text-neutral-500' },
  'sprite':           { label: 'Sprite',  bg: 'bg-coral-50', text: 'text-coral-600' },
  'temporal':         { label: 'Workflow', bg: 'bg-purple-50', text: 'text-purple-600' },
  'system':           { label: 'System',   bg: 'bg-neutral-50', text: 'text-neutral-500' },
  'hook':             { label: 'Hook',     bg: 'bg-amber-50', text: 'text-amber-600' },
  'dashboard':        { label: 'Manual',   bg: 'bg-green-50', text: 'text-green-600' },
  'ml':               { label: 'ML',       bg: 'bg-indigo-50', text: 'text-indigo-600' },
};

const SEVERITY_STYLE: Record<string, { bg: string; text: string }> = {
  info:     { bg: 'bg-neutral-50', text: 'text-neutral-500' },
  success:  { bg: 'bg-green-50',   text: 'text-green-600' },
  warning:  { bg: 'bg-amber-50',   text: 'text-amber-600' },
  error:    { bg: 'bg-red-50',     text: 'text-red-600' },
  critical: { bg: 'bg-red-100',    text: 'text-red-700' },
};

const CATEGORY_OPTIONS: UnifiedCategory[] = [
  'workflow', 'activity', 'timer', 'signal', 'child-workflow',
  'sprite', 'build', 'test', 'deploy', 'system',
];

/* ── Helpers ─────────────────────────────────────────────────────── */

function fmtTime(ts: string): string {
  return new Date(ts).toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    fractionalSecondDigits: 3, hour12: false,
  });
}

function fmtEventType(et: string): string {
  return et.replace(/^EVENT_TYPE_/, '').toLowerCase().replace(/_/g, ' ');
}

/** Group consecutive activity events by scheduledEventId for collapsible display */
function groupActivityEvents(events: UnifiedTimelineEvent[]): Array<{
  key: string;
  isGroup: boolean;
  events: UnifiedTimelineEvent[];
  activityType?: string;
  status?: string;
}> {
  const groups: ReturnType<typeof groupActivityEvents> = [];
  const activityGroups = new Map<number, UnifiedTimelineEvent[]>();

  for (const event of events) {
    if (event.source === 'temporal-history' && event.scheduledEventId && event.category === 'activity') {
      const groupKey = event.scheduledEventId;
      if (!activityGroups.has(groupKey)) activityGroups.set(groupKey, []);
      activityGroups.get(groupKey)!.push(event);
    }
  }

  // Groups with 2+ events are collapsible
  const groupedIds = new Set<number>();
  for (const [key, evts] of activityGroups) {
    if (evts.length >= 2) groupedIds.add(key);
  }

  for (const event of events) {
    const schedId = event.scheduledEventId;
    if (event.source === 'temporal-history' && schedId && groupedIds.has(schedId) && event.category === 'activity') {
      // Check if we already have a group for this
      const existing = groups.find(g => g.key === `act-${schedId}`);
      if (existing) {
        existing.events.push(event);
        // Update status from last event
        if (event.eventType.includes('COMPLETED')) existing.status = 'completed';
        else if (event.eventType.includes('FAILED')) existing.status = 'failed';
        else if (event.eventType.includes('STARTED')) existing.status = 'started';
      } else {
        groups.push({
          key: `act-${schedId}`,
          isGroup: true,
          events: [event],
          activityType: event.activityType,
          status: event.eventType.includes('SCHEDULED') ? 'scheduled' : 'started',
        });
      }
    } else {
      // Also check if this is the scheduled event that starts a group
      if (event.source === 'temporal-history' && event.temporalEventId && groupedIds.has(event.temporalEventId) && event.eventType.includes('SCHEDULED')) {
        const existing = groups.find(g => g.key === `act-${event.temporalEventId}`);
        if (existing) {
          existing.events.unshift(event);
        } else {
          groups.push({
            key: `act-${event.temporalEventId}`,
            isGroup: true,
            events: [event],
            activityType: event.activityType,
            status: 'scheduled',
          });
        }
      } else {
        groups.push({ key: `evt-${event.sequenceId}`, isGroup: false, events: [event] });
      }
    }
  }

  return groups;
}

export default function UnifiedEventTable({ events, className, activeFilterLabel, onClearExternalFilter }: UnifiedEventTableProps) {
  const [searchText, setSearchText] = useState('');
  const [filterSource, setFilterSource] = useState<UnifiedEventSource | ''>('');
  const [filterCategory, setFilterCategory] = useState<UnifiedCategory | ''>('');
  const [filterSeverity, setFilterSeverity] = useState<UnifiedSeverity | ''>('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedPayloads, setExpandedPayloads] = useState<Set<number>>(new Set());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Filter events
  const filtered = useMemo(() => {
    let result = events;
    if (filterSource) result = result.filter(e => e.source === filterSource);
    if (filterCategory) result = result.filter(e => e.category === filterCategory);
    if (filterSeverity) result = result.filter(e => e.severity === filterSeverity);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter(e =>
        e.eventType.toLowerCase().includes(q) ||
        e.title.toLowerCase().includes(q) ||
        e.description?.toLowerCase().includes(q) ||
        e.activityType?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [events, filterSource, filterCategory, filterSeverity, searchText]);

  // Group activity events
  const grouped = useMemo(() => groupActivityEvents(filtered), [filtered]);
  const visible = grouped.slice(0, visibleCount);

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const togglePayload = useCallback((seqId: number) => {
    setExpandedPayloads(prev => {
      const next = new Set(prev);
      if (next.has(seqId)) next.delete(seqId); else next.add(seqId);
      return next;
    });
  }, []);

  const clearFilters = () => {
    setFilterSource('');
    setFilterCategory('');
    setFilterSeverity('');
    setSearchText('');
  };

  const hasActiveFilters = filterSource || filterCategory || filterSeverity || searchText;

  return (
    <div className={`bg-white rounded-lg border border-neutral-200 shadow-warm-sm ${className || ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Event History</p>
          <span className="text-[10px] font-mono text-neutral-400">
            {filtered.length === events.length ? events.length : `${filtered.length}/${events.length}`}
          </span>
        </div>
        <button
          onClick={() => setShowFilters(v => !v)}
          className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors
            ${showFilters || hasActiveFilters
              ? 'bg-coral-50 text-coral-600'
              : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-50'
            }`}
        >
          <Filter size={12} />
          Filters
          {hasActiveFilters && (
            <span className="w-1.5 h-1.5 rounded-full bg-coral-500" />
          )}
        </button>
      </div>

      {/* Filter bar */}
      {showFilters && (
        <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[160px] max-w-[240px]">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search events..."
              className="w-full pl-7 pr-2 py-1.5 text-[11px] bg-neutral-50 border border-neutral-200 rounded focus:outline-none focus:border-coral-300 focus:ring-1 focus:ring-coral-200"
            />
          </div>
          <select
            value={filterSource}
            onChange={e => setFilterSource(e.target.value as any)}
            className="text-[11px] px-2 py-1.5 bg-neutral-50 border border-neutral-200 rounded focus:outline-none focus:border-coral-300"
          >
            <option value="">All Sources</option>
            <option value="temporal-history">Temporal</option>
            <option value="sprite">Sprite</option>
            <option value="system">System</option>
            <option value="hook">Hook</option>
            <option value="dashboard">Manual</option>
            <option value="custom-telemetry">Custom</option>
          </select>
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value as any)}
            className="text-[11px] px-2 py-1.5 bg-neutral-50 border border-neutral-200 rounded focus:outline-none focus:border-coral-300"
          >
            <option value="">All Categories</option>
            {CATEGORY_OPTIONS.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            value={filterSeverity}
            onChange={e => setFilterSeverity(e.target.value as any)}
            className="text-[11px] px-2 py-1.5 bg-neutral-50 border border-neutral-200 rounded focus:outline-none focus:border-coral-300"
          >
            <option value="">All Severities</option>
            <option value="info">Info</option>
            <option value="success">Success</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
            <option value="critical">Critical</option>
          </select>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600">
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {/* External filter chip */}
      {activeFilterLabel && (
        <div className="px-4 pb-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-coral-50 text-coral-700 border border-coral-200">
            Filtered: {activeFilterLabel}
            {onClearExternalFilter && (
              <button
                onClick={onClearExternalFilter}
                className="ml-0.5 p-0.5 rounded-full hover:bg-coral-100 transition-colors"
              >
                <X size={10} />
              </button>
            )}
          </span>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="border-b border-neutral-200 border-t border-neutral-100">
              <th className="text-left py-2 px-3 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider w-[32px]">#</th>
              <th className="text-left py-2 px-3 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider w-[90px]">Time</th>
              <th className="text-left py-2 px-3 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider">Event</th>
              <th className="text-left py-2 px-3 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider w-[70px]">Source</th>
              <th className="text-left py-2 px-3 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider w-[60px]">Severity</th>
              <th className="text-left py-2 px-3 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider">Details</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(group => {
              if (group.isGroup && group.events.length >= 2) {
                const isExpanded = expandedGroups.has(group.key);
                const summaryEvent = group.events[0];
                const lastEvent = group.events[group.events.length - 1];
                const statusIcon = group.status === 'completed' ? '\u2713' :
                  group.status === 'failed' ? '\u2717' : '\u22EF';

                return (
                  <tbody key={group.key}>
                    {/* Collapsed group header */}
                    <tr
                      className="border-b border-neutral-50 hover:bg-neutral-50/50 cursor-pointer transition-colors"
                      onClick={() => toggleGroup(group.key)}
                    >
                      <td className="py-1.5 px-3 text-neutral-300">
                        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </td>
                      <td className="py-1.5 px-3 font-mono text-neutral-400 whitespace-nowrap">
                        {fmtTime(summaryEvent.timestamp)}
                      </td>
                      <td className="py-1.5 px-3">
                        <span className="font-mono font-semibold text-neutral-700">
                          {group.activityType || 'Activity'}
                        </span>
                        <span className="ml-2 text-[9px] text-neutral-400">
                          {statusIcon} {group.events.length} events
                        </span>
                      </td>
                      <td className="py-1.5 px-3">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold ${SOURCE_BADGE['temporal-history'].bg} ${SOURCE_BADGE['temporal-history'].text}`}>
                          Temporal
                        </span>
                      </td>
                      <td className="py-1.5 px-3">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${(SEVERITY_STYLE[lastEvent.severity] || SEVERITY_STYLE.info).bg} ${(SEVERITY_STYLE[lastEvent.severity] || SEVERITY_STYLE.info).text}`}>
                          {lastEvent.severity}
                        </span>
                      </td>
                      <td className="py-1.5 px-3 text-neutral-400 truncate max-w-[300px]">
                        {group.activityType ? `${fmtEventType(summaryEvent.eventType)} \u2192 ${fmtEventType(lastEvent.eventType)}` : ''}
                      </td>
                    </tr>
                    {/* Expanded child rows */}
                    {isExpanded && group.events.map(event => (
                      <EventRow
                        key={event.sequenceId}
                        event={event}
                        indent
                        isPayloadExpanded={expandedPayloads.has(event.sequenceId)}
                        onTogglePayload={() => togglePayload(event.sequenceId)}
                      />
                    ))}
                  </tbody>
                );
              }

              // Single event row
              const event = group.events[0];
              return (
                <EventRow
                  key={group.key}
                  event={event}
                  isPayloadExpanded={expandedPayloads.has(event.sequenceId)}
                  onTogglePayload={() => togglePayload(event.sequenceId)}
                />
              );
            })}
          </tbody>
        </table>

        {/* Load more */}
        {visibleCount < grouped.length && (
          <div className="flex justify-center py-3 border-t border-neutral-100">
            <button
              onClick={() => setVisibleCount(v => v + PAGE_SIZE)}
              className="text-[11px] text-coral-600 font-semibold hover:text-coral-700 transition-colors"
            >
              Show more ({grouped.length - visibleCount} remaining)
            </button>
          </div>
        )}

        {filtered.length === 0 && (
          <div className="flex items-center justify-center py-8 text-[11px] text-neutral-400">
            {hasActiveFilters ? 'No events match the current filters' : 'No events recorded'}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Event Row ───────────────────────────────────────────────────── */

function EventRow({
  event,
  indent,
  isPayloadExpanded,
  onTogglePayload,
}: {
  event: UnifiedTimelineEvent;
  indent?: boolean;
  isPayloadExpanded: boolean;
  onTogglePayload: () => void;
}) {
  const sourceBadge = SOURCE_BADGE[event.source] || { label: event.source, bg: 'bg-neutral-50', text: 'text-neutral-500' };
  const sevStyle = SEVERITY_STYLE[event.severity] || SEVERITY_STYLE.info;

  const detailText = event.description
    || (event.payload ? Object.entries(event.payload).slice(0, 3).map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`).join(', ') : '');

  return (
    <>
      <tr className={`border-b border-neutral-50 hover:bg-neutral-50/50 transition-colors ${indent ? 'bg-neutral-25' : ''}`}>
        <td className="py-1.5 px-3 font-mono text-[9px] text-neutral-300">
          {indent && <span className="ml-2" />}
          {event.temporalEventId || event.customEventId || event.sequenceId}
        </td>
        <td className="py-1.5 px-3 font-mono text-neutral-400 whitespace-nowrap">
          {fmtTime(event.timestamp)}
        </td>
        <td className="py-1.5 px-3 font-mono font-semibold text-neutral-700 whitespace-nowrap">
          {indent && <span className="text-neutral-300 mr-1">{'\u2514'}</span>}
          {event.source === 'temporal-history' ? fmtEventType(event.eventType) : event.title}
        </td>
        <td className="py-1.5 px-3">
          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold ${sourceBadge.bg} ${sourceBadge.text}`}>
            {sourceBadge.label}
          </span>
        </td>
        <td className="py-1.5 px-3">
          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${sevStyle.bg} ${sevStyle.text}`}>
            {event.severity}
          </span>
        </td>
        <td className="py-1.5 px-3 text-neutral-400 max-w-[300px]">
          <span className="truncate block">{detailText || '\u2014'}</span>
          {event.payload && Object.keys(event.payload).length > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); onTogglePayload(); }}
              className="text-[9px] text-coral-500 hover:text-coral-600 font-semibold mt-0.5"
            >
              {isPayloadExpanded ? 'Hide JSON' : 'Show JSON'}
            </button>
          )}
        </td>
      </tr>
      {/* Expanded payload */}
      {isPayloadExpanded && event.payload && (
        <tr className="bg-neutral-50">
          <td colSpan={6} className="px-3 py-2">
            <pre className="text-[9px] font-mono text-neutral-600 bg-neutral-100 rounded p-2 overflow-x-auto max-h-[200px]">
              {JSON.stringify(event.payload, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}
